import React, { useState } from 'react';
import { Alert, Button, Modal, Progress, Space, Table, Tag, Upload, message } from 'antd';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { EmploymentType, User, UserCreateDTO } from '../../../../types/user.types';
import { RolesEnum } from '../../../../types/route.types';
import { DepartmentDto } from '../../../../types/department.types';
import { Store } from '../../../../types/shop.types';
import userService from '../../../../services/user/user.service';

interface EmployeeBulkImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => Promise<void> | void;
  departments: DepartmentDto[];
  shops: Store[];
  existingUsers: User[];
  companyId?: number;
  // Paketteki boş kullanıcı kontenjanı (bilinmiyorsa undefined)
  remainingSlots?: number;
}

type RowStatus = 'invalid' | 'ready' | 'success' | 'failed';

interface ParsedRow {
  rowNumber: number;
  fullName: string;
  email: string;
  departmentLabel: string;
  data?: Omit<UserCreateDTO, 'companyId'>;
  errors: string[];
  status: RowStatus;
  resultMessage?: string;
}

type ColumnKey =
  | 'firstName' | 'lastName' | 'email' | 'phone' | 'nationalId' | 'birthDate' | 'gender'
  | 'password' | 'role' | 'departments' | 'primaryDepartment' | 'branches' | 'title'
  | 'hireDate' | 'shiftType' | 'employeeType' | 'maritalStatus' | 'educationLevel'
  | 'city' | 'address' | 'emergencyContact' | 'annualLeaveLimit' | 'initialRemainingLeave';

interface TemplateColumn {
  key: ColumnKey;
  header: string;
  required: boolean;
  description: string;
}

const TEMPLATE_SHEET = 'Çalışanlar';
const EXAMPLE_EMAIL = 'ornek.calisan@ornek.com';
const MAX_ROWS = 500;
// TC'si bilinmeyen çalışanlar için yazılan numara; backend bunu "TC yok" olarak kabul ediyor,
// bu yüzden birden fazla satırda olabilir ve tekrar kontrolüne girmez.
const PLACEHOLDER_NATIONAL_ID = '11111111111';

// Başlıktaki "*" zorunlu alanı gösterir; eşleştirme yıldız ve parantezler atılarak yapılır.
const TEMPLATE_COLUMNS: TemplateColumn[] = [
  { key: 'firstName', header: 'Ad*', required: true, description: 'Çalışanın adı' },
  { key: 'lastName', header: 'Soyad*', required: true, description: 'Çalışanın soyadı' },
  { key: 'email', header: 'E-posta*', required: true, description: 'Giriş için kullanılacak e-posta adresi, her çalışan için farklı olmalı' },
  { key: 'phone', header: 'Telefon', required: false, description: 'Örn: 5321234567, boş bırakılabilir' },
  { key: 'nationalId', header: 'TC Kimlik No*', required: true, description: '11 haneli TC Kimlik No veya Yabancı Kimlik No' },
  { key: 'birthDate', header: 'Doğum Tarihi', required: false, description: 'GG.AA.YYYY formatında, örn: 15.03.1990, boş bırakılabilir' },
  { key: 'gender', header: 'Cinsiyet*', required: true, description: 'Erkek / Kadın / Diğer' },
  { key: 'password', header: 'Şifre*', required: true, description: 'En az 6 karakter, çalışan ilk girişte bu şifreyi kullanır' },
  { key: 'role', header: 'Rol*', required: true, description: 'Çalışan / Yönetici / Admin' },
  { key: 'departments', header: 'Departman', required: false, description: 'Admin dışındaki roller için zorunlu. Birden fazlaysa virgülle ayırın. İsimler "Departman ve Şubeler" sayfasındakilerle aynı olmalı' },
  { key: 'primaryDepartment', header: 'Ana Departman', required: false, description: 'Birden fazla departman yazıldıysa zorunlu; tek departmanda otomatik o seçilir' },
  { key: 'branches', header: 'Şube', required: false, description: 'Admin dışındaki roller için zorunlu. Birden fazlaysa virgülle ayırın' },
  { key: 'title', header: 'Pozisyon*', required: true, description: 'Örn: Satış Temsilcisi' },
  { key: 'hireDate', header: 'İşe Başlama Tarihi*', required: true, description: 'GG.AA.YYYY formatında, örn: 01.09.2026' },
  { key: 'shiftType', header: 'Vardiya Tipi*', required: true, description: 'Sabit / Vardiyalı' },
  { key: 'employeeType', header: 'Çalışma Alanı*', required: true, description: 'Ofis / Saha / Hibrit' },
  { key: 'maritalStatus', header: 'Medeni Hal', required: false, description: 'Bekar / Evli' },
  { key: 'educationLevel', header: 'Eğitim Durumu', required: false, description: 'İlköğretim / Lise / Lisans / Yüksek Lisans / Doktora' },
  { key: 'city', header: 'Şehir', required: false, description: 'Örn: İstanbul' },
  { key: 'address', header: 'Adres', required: false, description: 'Açık adres' },
  { key: 'emergencyContact', header: 'Acil Durum Kişisi', required: false, description: 'Örn: Ayşe Yılmaz - 5329876543' },
  { key: 'annualLeaveLimit', header: 'Yıllık İzin Hakkı (Gün)', required: false, description: 'Boş bırakılırsa şirket ayarındaki varsayılan kullanılır' },
  { key: 'initialRemainingLeave', header: 'Kalan Yıllık İzin (Gün)', required: false, description: 'Çalışanın başlangıçtaki kullanılabilir izin bakiyesi, boşsa 0' },
];

// Türkçe karakter ve büyük/küçük harf farkını yok sayarak karşılaştırma yapmak için
const normalize = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase('tr')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/\s+/g, ' ');

const normalizeHeader = (value: unknown) =>
  normalize(value).replace(/\*/g, '').replace(/\(.*?\)/g, '').trim();

const cellText = (value: unknown) => String(value ?? '').trim();

const GENDER_MAP: Record<string, UserCreateDTO['gender']> = {
  erkek: 'male', e: 'male', kadin: 'female', k: 'female', diger: 'other',
};
const ROLE_MAP: Record<string, RolesEnum> = {
  calisan: RolesEnum.USER, personel: RolesEnum.USER, kullanici: RolesEnum.USER,
  yonetici: RolesEnum.MANAGER, mudur: RolesEnum.MANAGER,
  admin: RolesEnum.ADMIN,
};
const SHIFT_MAP: Record<string, boolean> = {
  sabit: true, 'sabit vardiya': true, vardiyali: false, 'vardiya bazli': false,
};
const EMPLOYEE_TYPE_MAP: Record<string, EmploymentType> = {
  ofis: EmploymentType.OFFICE, saha: EmploymentType.FIELD, hibrit: EmploymentType.HYBRID,
};
const MARITAL_MAP: Record<string, UserCreateDTO['maritalStatus']> = {
  bekar: 'single', evli: 'married',
};
const EDUCATION_MAP: Record<string, UserCreateDTO['educationLevel']> = {
  ilkogretim: 'primary', ilkokul: 'primary', ortaokul: 'primary',
  lise: 'secondary', ortaogretim: 'secondary',
  lisans: 'bachelor', universite: 'bachelor', onlisans: 'bachelor',
  'yuksek lisans': 'master', doktora: 'phd',
};

const toIsoDate = (y: number, m: number, d: number): string | null => {
  const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const parsed = dayjs(iso);
  // dayjs 31.02 gibi tarihleri sonraki aya kaydırır; format karşılaştırmasıyla bunları eliyoruz
  return parsed.isValid() && parsed.format('YYYY-MM-DD') === iso ? iso : null;
};

// Excel tarih hücreleri seri numara olarak gelir; elle yazılanlar metin olarak gelir.
const parseDate = (value: unknown): string | null => {
  if (typeof value === 'number') {
    const code = XLSX.SSF.parse_date_code(value);
    return code?.y ? toIsoDate(code.y, code.m, code.d) : null;
  }
  const text = cellText(value);
  let match = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (match) return toIsoDate(Number(match[3]), Number(match[2]), Number(match[1]));
  match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) return toIsoDate(Number(match[1]), Number(match[2]), Number(match[3]));
  return null;
};

const splitList = (value: unknown) =>
  cellText(value).split(/[,;]/).map(item => item.trim()).filter(Boolean);

const getErrorMessage = (error: any): string =>
  error?.standartError?.description ||
  error?.standartError?.message ||
  error?.response?.data?.error?.description ||
  error?.message ||
  'Bilinmeyen bir hata oluştu.';

const EmployeeBulkImportModal: React.FC<EmployeeBulkImportModalProps> = ({
  open,
  onClose,
  onImported,
  departments,
  shops,
  existingUsers,
  companyId,
  remainingSlots,
}) => {
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const reset = () => {
    setRows([]);
    setFileName('');
    setImporting(false);
    setFinished(false);
    setProgress({ done: 0, total: 0 });
  };

  const handleDownloadTemplate = () => {
    const exampleDepartment = departments[0]?.name || 'Muhasebe';
    const exampleBranch = shops[0]?.name || 'Merkez';
    const exampleValues: Record<ColumnKey, string> = {
      firstName: 'Ahmet',
      lastName: 'Yılmaz',
      email: EXAMPLE_EMAIL,
      phone: '5321234567',
      nationalId: '12345678901',
      birthDate: '15.03.1990',
      gender: 'Erkek',
      password: 'Sifre123',
      role: 'Çalışan',
      departments: exampleDepartment,
      primaryDepartment: exampleDepartment,
      branches: exampleBranch,
      title: 'Satış Temsilcisi',
      hireDate: dayjs().format('DD.MM.YYYY'),
      shiftType: 'Sabit',
      employeeType: 'Ofis',
      maritalStatus: 'Evli',
      educationLevel: 'Lisans',
      city: 'İstanbul',
      address: 'Örnek Mah. Örnek Sok. No:1',
      emergencyContact: 'Ayşe Yılmaz - 5329876543',
      annualLeaveLimit: '14',
      initialRemainingLeave: '0',
    };

    const employeesSheet = XLSX.utils.aoa_to_sheet([
      TEMPLATE_COLUMNS.map(column => column.header),
      TEMPLATE_COLUMNS.map(column => exampleValues[column.key]),
    ]);
    employeesSheet['!cols'] = TEMPLATE_COLUMNS.map(column => ({ wch: Math.max(column.header.length + 4, 18) }));

    const guideSheet = XLSX.utils.aoa_to_sheet([
      ['Alan', 'Zorunlu', 'Açıklama / Geçerli Değerler'],
      ...TEMPLATE_COLUMNS.map(column => [
        column.header.replace('*', ''),
        column.required ? 'Evet' : 'Hayır',
        column.description,
      ]),
      [],
      ['Not', '', `"${TEMPLATE_SHEET}" sayfasındaki örnek satırı silip kendi çalışanlarınızı ekleyin. Başlık satırını değiştirmeyin.`],
    ]);
    guideSheet['!cols'] = [{ wch: 26 }, { wch: 10 }, { wch: 110 }];

    const listLength = Math.max(departments.length, shops.length);
    const namesSheet = XLSX.utils.aoa_to_sheet([
      ['Departmanlar', 'Şubeler'],
      ...Array.from({ length: listLength }, (_, i) => [departments[i]?.name || '', shops[i]?.name || '']),
    ]);
    namesSheet['!cols'] = [{ wch: 36 }, { wch: 36 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, employeesSheet, TEMPLATE_SHEET);
    XLSX.utils.book_append_sheet(workbook, guideSheet, 'Açıklamalar');
    XLSX.utils.book_append_sheet(workbook, namesSheet, 'Departman ve Şubeler');
    XLSX.writeFile(workbook, 'Calisan_Ekleme_Sablonu.xlsx');
  };

  const buildRow = (cells: unknown[], columnIndex: Partial<Record<ColumnKey, number>>, rowNumber: number): ParsedRow => {
    const get = (key: ColumnKey) => {
      const index = columnIndex[key];
      return index === undefined ? '' : cells[index];
    };
    const errors: string[] = [];

    const firstName = cellText(get('firstName'));
    const lastName = cellText(get('lastName'));
    const email = cellText(get('email')).toLowerCase();
    const phone = cellText(get('phone')).replace(/\s/g, '');
    const nationalId = cellText(get('nationalId')).replace(/\s/g, '');
    const password = cellText(get('password'));
    const title = cellText(get('title'));

    if (!firstName) errors.push('Ad boş olamaz');
    if (!lastName) errors.push('Soyad boş olamaz');
    if (!email) errors.push('E-posta boş olamaz');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push('E-posta geçersiz');
    else if (email === EXAMPLE_EMAIL) errors.push('Şablondaki örnek satır silinmeli');
    if (!nationalId) errors.push('TC Kimlik No boş olamaz');
    else if (!/^\d{11}$/.test(nationalId)) errors.push('TC Kimlik No 11 haneli rakam olmalı');
    if (!password) errors.push('Şifre boş olamaz');
    else if (password.length < 6) errors.push('Şifre en az 6 karakter olmalı');
    if (!title) errors.push('Pozisyon boş olamaz');

    // Telefon ve doğum tarihi backend'de opsiyonel; sadece yazılmışsa formatı kontrol ediliyor
    const birthDate = parseDate(get('birthDate'));
    if (cellText(get('birthDate')) && !birthDate) errors.push('Doğum tarihi geçersiz (GG.AA.YYYY)');
    const hireDate = parseDate(get('hireDate'));
    if (!hireDate) errors.push('İşe başlama tarihi boş veya geçersiz (GG.AA.YYYY)');

    const gender = GENDER_MAP[normalize(get('gender'))];
    if (!gender) errors.push('Cinsiyet Erkek / Kadın / Diğer olmalı');

    const role = ROLE_MAP[normalize(get('role'))];
    if (!role) errors.push('Rol Çalışan / Yönetici / Admin olmalı');

    const isFixedShift = SHIFT_MAP[normalize(get('shiftType'))];
    if (isFixedShift === undefined) errors.push('Vardiya tipi Sabit / Vardiyalı olmalı');

    const employeeType = EMPLOYEE_TYPE_MAP[normalize(get('employeeType'))];
    if (!employeeType) errors.push('Çalışma alanı Ofis / Saha / Hibrit olmalı');

    let maritalStatus: UserCreateDTO['maritalStatus'];
    if (cellText(get('maritalStatus'))) {
      maritalStatus = MARITAL_MAP[normalize(get('maritalStatus'))];
      if (!maritalStatus) errors.push('Medeni hal Bekar / Evli olmalı');
    }

    let educationLevel: UserCreateDTO['educationLevel'];
    if (cellText(get('educationLevel'))) {
      educationLevel = EDUCATION_MAP[normalize(get('educationLevel'))];
      if (!educationLevel) errors.push('Eğitim durumu geçersiz');
    }

    const departmentIds: number[] = [];
    const departmentNames = splitList(get('departments'));
    departmentNames.forEach(name => {
      const department = departments.find(d => normalize(d.name) === normalize(name));
      if (department) departmentIds.push(department.id);
      else errors.push(`Departman bulunamadı: ${name}`);
    });

    const branchIds: number[] = [];
    const branchNames = splitList(get('branches'));
    branchNames.forEach(name => {
      const branch = shops.find(s => normalize(s.name) === normalize(name));
      if (branch) branchIds.push(branch.id);
      else errors.push(`Şube bulunamadı: ${name}`);
    });

    // Çalışan ekleme formunda olduğu gibi departman/şube sadece admin için opsiyonel
    if (role && role !== RolesEnum.ADMIN) {
      if (departmentNames.length === 0) errors.push('Departman boş olamaz');
      if (branchNames.length === 0) errors.push('Şube boş olamaz');
    }

    let primaryDepartmentId: number | undefined;
    const primaryName = cellText(get('primaryDepartment'));
    if (primaryName) {
      const primary = departments.find(d => normalize(d.name) === normalize(primaryName));
      if (!primary) errors.push(`Ana departman bulunamadı: ${primaryName}`);
      else if (departmentIds.length > 0 && !departmentIds.includes(primary.id)) errors.push('Ana departman, Departman sütununda da yazılı olmalı');
      else primaryDepartmentId = primary.id;
    } else if (departmentIds.length === 1) {
      primaryDepartmentId = departmentIds[0];
    } else if (departmentIds.length > 1) {
      errors.push('Birden fazla departman varsa Ana Departman zorunlu');
    }

    const parseDays = (key: ColumnKey, label: string): number | undefined => {
      const text = cellText(get(key));
      if (!text) return undefined;
      const days = Number(text.replace(',', '.'));
      if (!Number.isFinite(days) || days < 0 || days > 365) {
        errors.push(`${label} 0-365 arasında bir sayı olmalı`);
        return undefined;
      }
      return days;
    };
    const annualLeaveLimit = parseDays('annualLeaveLimit', 'Yıllık izin hakkı');
    const initialRemainingLeave = parseDays('initialRemainingLeave', 'Kalan yıllık izin');

    const base = {
      rowNumber,
      fullName: `${firstName} ${lastName}`.trim() || '-',
      email: email || '-',
      departmentLabel: departmentNames.join(', ') || '-',
    };

    if (errors.length > 0) {
      return { ...base, errors, status: 'invalid' };
    }

    return {
      ...base,
      errors,
      status: 'ready',
      data: {
        firstName,
        lastName,
        email,
        phone: phone || undefined,
        nationalId,
        password,
        title,
        role: role!,
        gender,
        birthDate: (birthDate || undefined) as unknown as Date,
        hireDate: hireDate as unknown as Date,
        isFixedShift,
        employeeType,
        maritalStatus,
        educationLevel,
        department: departmentIds.length ? (departmentIds as unknown as number) : null,
        branch: branchIds.length ? (branchIds as unknown as number) : null,
        primaryDepartmentId,
        city: cellText(get('city')) || undefined,
        address: cellText(get('address')) || undefined,
        emergencyContact: cellText(get('emergencyContact')) || undefined,
        annualLeaveLimit,
        initialRemainingLeave,
      } as Omit<UserCreateDTO, 'companyId'>,
    };
  };

  const handleFile = async (file: File) => {
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheetName =
        workbook.SheetNames.find(name => normalize(name) === normalize(TEMPLATE_SHEET)) || workbook.SheetNames[0];
      const sheetRows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
        header: 1,
        defval: '',
        blankrows: false,
      });

      if (sheetRows.length < 2) {
        message.error('Dosyada çalışan satırı bulunamadı.');
        return;
      }

      const headers = sheetRows[0].map(normalizeHeader);
      const columnIndex: Partial<Record<ColumnKey, number>> = {};
      TEMPLATE_COLUMNS.forEach(column => {
        const index = headers.indexOf(normalizeHeader(column.header));
        if (index !== -1) columnIndex[column.key] = index;
      });

      const missingColumns = TEMPLATE_COLUMNS.filter(column => column.required && columnIndex[column.key] === undefined);
      if (missingColumns.length > 0) {
        message.error(`Dosyada şu sütunlar eksik: ${missingColumns.map(c => c.header.replace('*', '')).join(', ')}. Lütfen örnek şablonu kullanın.`);
        return;
      }

      const dataRows = sheetRows
        .map((cells, index) => ({ cells, rowNumber: index + 1 }))
        .slice(1)
        .filter(({ cells }) => cells.some(cell => cellText(cell) !== ''));

      if (dataRows.length === 0) {
        message.error('Dosyada çalışan satırı bulunamadı.');
        return;
      }
      if (dataRows.length > MAX_ROWS) {
        message.error(`Tek seferde en fazla ${MAX_ROWS} çalışan eklenebilir. Dosyada ${dataRows.length} satır var.`);
        return;
      }

      const parsed = dataRows.map(({ cells, rowNumber }) => buildRow(cells, columnIndex, rowNumber));

      // Dosya içi ve mevcut çalışanlarla çakışan e-posta / TC kontrolü
      const existingEmails = new Set(existingUsers.map(u => (u.email || '').toLowerCase()));
      const existingNationalIds = new Set(existingUsers.map(u => String(u.nationalId || '')).filter(Boolean));
      const countBy = (values: string[]) =>
        values.reduce<Record<string, number>>((acc, value) => ({ ...acc, [value]: (acc[value] || 0) + 1 }), {});
      const emailCounts = countBy(parsed.map(row => row.data?.email || '').filter(Boolean));
      const nationalIdCounts = countBy(parsed.map(row => row.data?.nationalId || '').filter(Boolean));

      parsed.forEach(row => {
        if (!row.data) return;
        const { email, nationalId } = row.data;
        if (emailCounts[email] > 1) row.errors.push('Bu e-posta dosyada birden fazla satırda var');
        const checkNationalId = nationalId && nationalId !== PLACEHOLDER_NATIONAL_ID;
        if (checkNationalId && nationalIdCounts[nationalId] > 1) row.errors.push('Bu TC Kimlik No dosyada birden fazla satırda var');
        if (existingEmails.has(email)) row.errors.push('Bu e-posta ile kayıtlı bir çalışan zaten var');
        if (checkNationalId && existingNationalIds.has(nationalId)) row.errors.push('Bu TC Kimlik No ile kayıtlı bir çalışan zaten var');
        if (row.errors.length > 0) {
          row.status = 'invalid';
          row.data = undefined;
        }
      });

      setFileName(file.name);
      setRows(parsed);
      setFinished(false);
    } catch (error) {
      message.error('Dosya okunamadı. Lütfen geçerli bir Excel dosyası yükleyin.');
    }
  };

  const handleImport = async () => {
    if (!companyId) {
      message.error('Şirket bilgisi bulunamadı.');
      return;
    }
    const targets = rows.filter(row => row.status === 'ready');
    if (targets.length === 0) return;

    setImporting(true);
    setProgress({ done: 0, total: targets.length });
    let successCount = 0;

    // Sunucudaki kullanıcı limiti / e-posta çakışması kontrolleri sıralı çalışsın diye tek tek gönderiyoruz
    for (const target of targets) {
      let status: RowStatus = 'success';
      let resultMessage: string | undefined;
      try {
        await userService.createUser({ ...target.data!, companyId });
        successCount++;
      } catch (error) {
        status = 'failed';
        resultMessage = getErrorMessage(error);
      }
      setRows(prev => prev.map(row => (row.rowNumber === target.rowNumber ? { ...row, status, resultMessage } : row)));
      setProgress(prev => ({ ...prev, done: prev.done + 1 }));
    }

    setImporting(false);
    setFinished(true);

    if (successCount === targets.length) {
      message.success(`${successCount} çalışan başarıyla eklendi.`);
    } else if (successCount > 0) {
      message.warning(`${successCount} çalışan eklendi, ${targets.length - successCount} çalışan eklenemedi.`);
    } else {
      message.error('Hiçbir çalışan eklenemedi. Hata detaylarını tabloda görebilirsiniz.');
    }

    if (successCount > 0) {
      await onImported();
    }
  };

  const readyCount = rows.filter(row => row.status === 'ready').length;
  const invalidCount = rows.filter(row => row.status === 'invalid').length;
  const successCount = rows.filter(row => row.status === 'success').length;
  const failedCount = rows.filter(row => row.status === 'failed').length;

  const columns = [
    { title: 'Satır', dataIndex: 'rowNumber', key: 'rowNumber', width: 70 },
    { title: 'Ad Soyad', dataIndex: 'fullName', key: 'fullName', width: 170 },
    { title: 'E-posta', dataIndex: 'email', key: 'email', width: 220 },
    { title: 'Departman', dataIndex: 'departmentLabel', key: 'departmentLabel', width: 160 },
    {
      title: 'Durum',
      key: 'status',
      render: (_: unknown, row: ParsedRow) => {
        switch (row.status) {
          case 'ready':
            return <Tag color="blue">Hazır</Tag>;
          case 'success':
            return <Tag color="green">Eklendi</Tag>;
          case 'failed':
            return (
              <>
                <Tag color="red">Eklenemedi</Tag>
                <div className="text-danger small mt-1">{row.resultMessage}</div>
              </>
            );
          default:
            return (
              <>
                <Tag color="red">Hatalı</Tag>
                <ul className="text-danger small mb-0 mt-1 ps-3">
                  {row.errors.map(error => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </>
            );
        }
      },
    },
  ];

  return (
    <Modal
      title="Excel ile Çalışan Ekle"
      open={open}
      onCancel={() => !importing && onClose()}
      afterClose={reset}
      maskClosable={!importing}
      closable={!importing}
      width={1000}
      footer={
        <Space>
          <Button onClick={onClose} disabled={importing}>
            {finished ? 'Kapat' : 'İptal'}
          </Button>
          {rows.length > 0 && !finished && (
            <Button onClick={reset} disabled={importing}>
              Farklı Dosya Seç
            </Button>
          )}
          {rows.length > 0 && !finished && (
            <Button type="primary" onClick={handleImport} loading={importing} disabled={readyCount === 0}>
              {readyCount} Çalışanı Ekle
            </Button>
          )}
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message="Nasıl kullanılır?"
        description={
          <ol className="mb-0 ps-3">
            <li>Örnek Excel şablonunu indirin. "Açıklamalar" sayfasında her sütunun geçerli değerleri yazıyor.</li>
            <li>Örnek satırı silip çalışanlarınızı ekleyin. Departman ve şube isimleri sistemdekiyle aynı olmalı.</li>
            <li>Dosyayı yükleyin, kontrol sonucunu inceleyin ve hazır olan çalışanları ekleyin.</li>
          </ol>
        }
      />

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <Button icon={<i className="ti ti-download" />} onClick={handleDownloadTemplate}>
          Örnek Excel İndir
        </Button>
        {fileName && (
          <span className="text-muted">
            <i className="ti ti-file-spreadsheet me-1" />
            {fileName}
          </span>
        )}
      </div>

      {rows.length === 0 ? (
        <Upload.Dragger
          accept=".xlsx,.xls,.csv"
          multiple={false}
          showUploadList={false}
          beforeUpload={file => {
            handleFile(file);
            return Upload.LIST_IGNORE;
          }}
        >
          <p className="ant-upload-drag-icon mb-2">
            <i className="ti ti-file-spreadsheet" style={{ fontSize: 40, color: '#7338A0' }} />
          </p>
          <p className="ant-upload-text">Excel dosyasını buraya sürükleyin veya seçmek için tıklayın</p>
          <p className="ant-upload-hint">.xlsx, .xls veya .csv — tek seferde en fazla {MAX_ROWS} çalışan</p>
        </Upload.Dragger>
      ) : (
        <>
          <Space wrap className="mb-3">
            <Tag>Toplam: {rows.length}</Tag>
            {!finished && <Tag color="blue">Hazır: {readyCount}</Tag>}
            {invalidCount > 0 && <Tag color="red">Hatalı: {invalidCount}</Tag>}
            {successCount > 0 && <Tag color="green">Eklendi: {successCount}</Tag>}
            {failedCount > 0 && <Tag color="red">Eklenemedi: {failedCount}</Tag>}
          </Space>

          {!finished && invalidCount > 0 && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={`${invalidCount} satırda hata var. Bu satırlar eklenmeyecek; düzeltip dosyayı tekrar yükleyebilir veya sadece hazır olanları ekleyebilirsiniz.`}
            />
          )}

          {!finished && remainingSlots !== undefined && readyCount > remainingSlots && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={`Paketinizde ${Math.max(remainingSlots, 0)} kişilik boş yer var. Limit dolduktan sonraki çalışanlar eklenemeyecek.`}
            />
          )}

          {(importing || finished) && (
            <Progress
              className="mb-3"
              percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0}
              status={importing ? 'active' : failedCount > 0 ? 'exception' : 'success'}
            />
          )}

          <Table
            rowKey="rowNumber"
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 800 }}
          />
        </>
      )}
    </Modal>
  );
};

export default EmployeeBulkImportModal;
